import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Download, ArrowRight, Code, Sparkles } from 'lucide-react';

const Hero = () => {
  const [displayText, setDisplayText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const fullText = "Hi, I'm Shivendu";

  useEffect(() => {
    if (currentIndex < fullText.length) {
      const timeout = setTimeout(() => {
        setDisplayText(prev => prev + fullText[currentIndex]);
        setCurrentIndex(prev => prev + 1);
      }, 150);
      return () => clearTimeout(timeout);
    }
  }, [currentIndex]);

  const scrollToProjects = () => {
    const element = document.getElementById('projects');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleResumeDownload = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/resume/download`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = 'Shivendu_Sinha_Resume.pdf';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        throw new Error('Resume not available');
      }
    } catch (error) {
      console.error('Resume download failed:', error);
      alert('Resume download is temporarily unavailable. Please contact me directly at shivendu0103@gmail.com');
    }
  };

  return (
    <section id="hero" className="min-h-screen flex items-center justify-center relative overflow-hidden pt-16">
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className="absolute animate-pulse"
            style={{
              left: `${20 + i * 15}%`,
              top: `${20 + (i % 3) * 25}%`,
              animationDelay: `${i * 0.5}s`,
              animationDuration: `${3 + i}s`
            }}
          >
            <Code className="w-6 h-6 text-blue-300/30 dark:text-cyan-300/20" />
          </div>
        ))}
        <div className="absolute top-20 right-20 w-32 h-32 border border-blue-200/30 dark:border-cyan-300/20 rounded-full animate-spin-slow"></div>
        <div className="absolute bottom-32 left-20 w-16 h-16 bg-gradient-to-r from-blue-400/10 to-cyan-400/10 rounded-lg animate-bounce-slow"></div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="space-y-8 animate-fade-in-up">
            <div className="space-y-4">
              <h1 className="text-5xl lg:text-6xl font-bold text-slate-900 dark:text-white">
                <span className="typewriter">{displayText}</span>
                <span className="animate-blink">|</span>
              </h1>
              <div className="text-xl lg:text-2xl font-medium">
                <span className="bg-gradient-to-r from-blue-600 via-cyan-500 to-green-500 bg-clip-text text-transparent animate-gradient">
                  Computer Science Student & Full-Stack Developer
                </span>
              </div>
            </div>
            <p className="text-lg text-slate-600 dark:text-slate-300 leading-relaxed max-w-2xl">
              A passionate Computer Science student who loves turning ideas into reality. 
              Whether it's building cultural heritage platforms or creating AI-powered tools, 
              I thrive on solving real-world problems through code.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={scrollToProjects}
                size="lg"
                className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white px-8 py-3 rounded-full font-semibold transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                View My Work
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button
                onClick={handleResumeDownload}
                variant="outline"
                size="lg"
                className="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white px-8 py-3 rounded-full font-semibold transition-all duration-300 hover:scale-105"
              >
                <Download className="w-5 h-5 mr-2" />
                Download Resume
              </Button>
            </div>
          </div>

          {/* Profile Image */}
          <div className="flex justify-center lg:justify-end animate-fade-in-up-delay">
            <div className="relative">
              {/* Glowing background for depth */}
              <div className="absolute inset-0 rounded-full bg-gradient-to-t from-blue-500/20 to-cyan-500/20 blur-3xl scale-110 -z-10" />
              {/* Pop-out profile image */}
              <div className="relative w-80 h-80 lg:w-96 lg:h-96 rounded-full overflow-visible border-4 border-white dark:border-slate-800 shadow-2xl group">
                <img
                  src="profile.png" // Your profile photo path
  alt="Shivendu Sinha"
  className="
    absolute left-1/2 top-1/2 -translate-x-1/2         // Perfectly center the image inside the circular container
    -translate-y-[56%]                                 // Gently pop upward on mobile
    sm:-translate-y-[32%]                              // More pop on small tablets
    md:-translate-y-[46%]                              // Even more pop on medium screens
    lg:-translate-y-[62%]                              // Dramatic pop on large screens/laptops
    w-[270px] h-[350px]                                // Mobile size: big but not overwhelming
    sm:w-[240px] sm:h-[360px]                          // Small tablet size
    md:w-[320px] md:h-[480px]                          // Medium screen
    lg:w-[355px] lg:h-[510px]                          // Laptop/full size (bold pop)
    object-cover rounded-[39%]                         // Crop creatively; makes the head/shoulders project nicely
    shadow-2xl shadow-blue-50/40                       // 3D shadow and subtle blue glow
    group-hover:scale-105 transition-transform duration-500 // Smooth pop on hover
  "
  style={{ zIndex: 1 }} // Make sure it’s above other elements in the stack
/>
              </div>
              {/* Floating Tech Icons */}
              <div className="absolute -top-4 -left-4 bg-white dark:bg-slate-800 p-3 rounded-full shadow-lg animate-bounce-slow">
                <Code className="w-6 h-6 text-blue-600" />
              </div>
              <div className="absolute -bottom-4 -right-4 bg-white dark:bg-slate-800 p-3 rounded-full shadow-lg animate-bounce-slow-delay">
                <Sparkles className="w-6 h-6 text-green-500" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-slate-400 dark:border-slate-500 rounded-full flex justify-center">
          <div className="w-1 h-3 bg-slate-400 dark:bg-slate-500 rounded-full mt-2 animate-scroll-indicator"></div>
        </div>
      </div>

      <style jsx>{`
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        @keyframes bounce-slow-delay {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes fade-in-up-delay {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
        @keyframes gradient {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes scroll-indicator {
          0% { opacity: 0; transform: translateY(0); }
          50% { opacity: 1; transform: translateY(5px); }
          100% { opacity: 0; transform: translateY(10px); }
        }
        .animate-spin-slow { animation: spin-slow 20s linear infinite; }
        .animate-bounce-slow { animation: bounce-slow 3s ease-in-out infinite; }
        .animate-bounce-slow-delay { animation: bounce-slow-delay 3s ease-in-out infinite 1s; }
        .animate-fade-in-up { animation: fade-in-up 1s ease-out; }
        .animate-fade-in-up-delay { animation: fade-in-up-delay 1s ease-out 0.3s both; }
        .animate-blink { animation: blink 1s infinite; }
        .animate-gradient { background-size: 300% 300%; animation: gradient 6s ease infinite; }
        .animate-scroll-indicator { animation: scroll-indicator 2s ease-in-out infinite; }
      `}</style>
    </section>
  );
};

export default Hero;
